/**
 * The "Back to <app>" button on the terminal reset screens.
 *
 * WHAT THE HOSTED SCREEN DOES, observed live 2026-10-13 from a real RO-A
 * email link and from Management API tickets:
 *
 *   label  "Back to " + the CLIENT NAME, so RO-A reads
 *          "Back to CareSuper Register Online" (auth0_client.register_online).
 *          Only when there is no client at all (e.g. a garbage ticket) does it
 *          fall back to the tenant friendly name: "Back to CareSuper".
 *   href   the client's initiate_login_uri, else the tenant
 *          default_redirection_uri, with ?iss=<this origin>/ appended. Both
 *          are var.member_portal_login_uri = https://app.azuremark.co/login,
 *          and register_online has no initiate_login_uri, so RO-A always gets
 *          the tenant default.
 *
 * ARCHITECTURE.md previously recorded "Back to CareSuper" for RO-A. That's
 * the clientless case. The live RO-A screen shows the client name.
 *
 * WHERE THE HREF COMES FROM. Advanced rendering replaces Auth0's own drawing
 * of this button (ARCHITECTURE.md, "Interaction with an ACUL
 * reset-password-success screen"). If the ACUL context carries the resolved
 * URL in screen.links it's used as-is. Otherwise the same value Terraform
 * gives both fields is used: keep FALLBACK_LOGIN_URI in step with
 * var.member_portal_login_uri.
 */
export const FALLBACK_LOGIN_URI = "https://app.azuremark.co/login";

// `back_to_app` is the key Auth0 actually sends. Confirmed in the live context
// of reset-password-error on 2026-10-13: screen.links.back_to_app =
// "https://app.azuremark.co/login?iss=https%3A%2F%2Fauth.azuremark.co%2F". It
// carries the tenant's own resolution (client initiate_login_uri, else
// default_redirection_uri), so it's preferred over the fallback below. The
// other keys are defensive only.
const LINK_KEYS = [
  "back_to_app",
  "back_to_login",
  "backToLogin",
  "login",
  "loginLink",
  "back",
];

export function resolveBackHref(
  links: Record<string, string> | null | undefined
): string {
  for (const key of LINK_KEYS) {
    const value = links?.[key];
    if (typeof value === "string" && /^https:\/\//.test(value)) return value;
  }
  const url = new URL(FALLBACK_LOGIN_URI);
  // Auth0 appends the issuer because initiate_login_uri doubles as the OIDC
  // third-party-initiated-login endpoint. Login.jsx ignores it, but it's
  // reproduced so the two renderings send the member to the identical URL.
  url.searchParams.set("iss", `${window.location.origin}/`);
  return url.toString();
}

export function resolveBackLabel(
  clientName: string | null | undefined,
  tenantFriendlyName: string | null | undefined,
  template = "Back to {{appName}}"
): string {
  const appName =
    clientName?.trim() || tenantFriendlyName?.trim() || "CareSuper";
  return template.replace("{{appName}}", appName);
}
